<script setup lang="ts">
import { computed, ref } from 'vue'
import _ from 'lodash'

type IconVariant = 'primary' | 'neutral' | 'subtle'

interface Props {
  width?: string
  height?: string
  variant?: IconVariant
  tooltip?: string
  tooltipLocation?: string
  active?: boolean
  disabled?: boolean
  class?: string
  rounded?: boolean
  hasBackground?: boolean
  hasBorder?: boolean
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  width: 'fit-content',
  height: 'fit-content',
  variant: 'primary',
  tooltip: '',
  tooltipLocation: 'bottom',
  active: false,
  disabled: false,
  class: '',
  rounded: false,
  hasBackground: false,
  hasBorder: true,
  ariaLabel: undefined,
})

const emit = defineEmits(['click'])

const isHovered = ref(false)
const isActive = computed(() => props.active || isHovered.value)

const STYLES = {
  primary: {
    normal: ['tw:before:border-s-d-s-dark-border-default-primary'],
    hover: 'tw:group-hover:before:border-brand-mint-green',
    active: 'tw:before:!border-brand-mint-green',
    disabled: {
      bg: 'tw:before:bg-n-s-g-dark-background-surface-default',
      border: 'tw:before:border-s-d-s-dark-border-default-primary',
      icon: 'tw:text-[#777777]',
    },
  },
  neutral: {
    normal: [
      'tw:before:bg-s-d-s-dark-background-opacity-200',
      'tw:before:border-s-d-s-dark-border-default-primary',
    ],
    hover: [
      'tw:group-hover:before:border-n-s-g-dark-border-default-primary',
      'tw:group-hover:before:bg-gray-900',
    ],
    active: ['tw:before:!border-n-s-g-dark-border-default-primary', 'tw:before:bg-gray-900'],
    disabled: {
      bg: 'tw:before:bg-s-d-s-dark-background-disabled-default',
      border: 'tw:before:border-s-d-s-dark-border-disabled-default',
      icon: 'tw:text-s-d-s-dark-icon-disabled-default',
    },
  },
  subtle: {
    normal: '',
    hover: 'tw:group-hover:before:bg-transparent',
    active: 'tw:before:bg-gray-800',
    disabled: {
      icon: 'tw:text-s-d-s-dark-icon-disabled-default',
    },
  },
} as const

const style = computed(() => STYLES[props.variant])

const bgClasses = computed(() => {
  const base = [
    "tw:before:content-['']",
    'tw:before:absolute tw:before:inset-0 tw:before:z-0',
    'tw:before:transition-all tw:before:duration-500',
  ]

  if (props.disabled) {
    const d = style.value.disabled as any
    return _.compact(_.flatten([...base, d.bg || '', d.border || ''])).join(' ')
  }

  // When active, don't apply normal border classes, only active ones
  const normalClasses =
    isActive.value && Array.isArray(style.value.normal)
      ? style.value.normal.filter((cls: string) => !cls.includes('border'))
      : style.value.normal

  const classes = [
    ...base,
    normalClasses,
    isActive.value && style.value.active,
    style.value.hover,
    props.variant !== 'subtle' && 'tw:before:border',
    props.rounded ? 'tw:before:rounded-radius-100' : 'tw:before:rounded-radius-50 ',
    props.hasBackground
      ? isActive.value
        ? 'tw:before:bg-s-d-s-dark-background-opacity-200'
        : 'tw:before:bg-s-d-s-dark-background-opacity-100"'
      : '',
    // hasBorder: false -> ẩn border mặc định, chỉ hiện khi hover
    !props.hasBorder && 'tw:before:border-transparent',
    !props.hasBorder && 'tw:group-hover:before:border-brand-mint-green',
  ]

  return _.compact(_.flatten(classes)).join(' ')
})

const iconColorClass = computed(() => {
  // disabled thì ưu tiên màu disabled
  if (props.disabled) {
    const d = style.value.disabled as any
    return d.icon || 'tw:text-s-d-s-dark-icon-disabled-default'
  }

  // đang active hoặc hover -> icon xanh
  if (isActive.value) {
    return 'tw:text-s-d-s-dark-icon-brand-green tw>'
  }

  // trạng thái bình thường
  return ''
})

const cursorClass = computed(() => (props.disabled ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer'))
</script>

<template>
  <div
    class="tw:relative tw:inline-block tw:group tw:select-none"
    :class="[
      props.class,
      cursorClass,
      { 'active-state': isActive, 'disabled-state': props.disabled },
    ]"
    :style="{ width: props.width, height: props.height }"
    role="button"
    tabindex="0"
    :aria-label="props.ariaLabel || props.tooltip || undefined"
    :aria-disabled="props.disabled"
    @click.stop="!props.disabled && emit('click', $event)"
    @keydown.enter.stop="!props.disabled && emit('click', $event)"
    @keydown.space.prevent.stop="!props.disabled && emit('click', $event)"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div :class="bgClasses"></div>

    <div
      class="tw:relative tw:flex tw:items-center tw:justify-center tw:w-full tw:h-full tw:transition-all tw:duration-500"
      :class="iconColorClass"
    >
      <slot name="icon"></slot>
    </div>

    <v-tooltip
      v-if="props.tooltip"
      activator="parent"
      :location="props.tooltipLocation as any"
      eager
    >
      {{ props.tooltip }}
    </v-tooltip>
  </div>
</template>

<style scoped>
:deep(svg) {
  path {
    stroke: var(--sds-color-icon-secondary-default);
    transition: stroke 0.3s ease;
  }
}

/* Hover */
.group:hover :deep(svg) path {
  stroke: var(--sds-color-icon-brand-green);
}

/* Active (props.active = true) */
.active-state :deep(svg) path {
  stroke: var(--sds-color-icon-brand-green);
}

/* Disabled */
.disabled-state :deep(svg) path {
  stroke: var(--sds-color-icon-disabled-default);
}

/* Hover */
.group:hover :deep(svg) circle {
  stroke: var(--sds-color-icon-brand-green);
}

/* Active (props.active = true) */
.active-state :deep(svg) circle {
  stroke: var(--sds-color-icon-brand-green);
}

/* Disabled */
.disabled-state :deep(svg) circle {
  stroke: var(--sds-color-icon-disabled-default);
}
</style>
